import { Avatar, Segmented } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { InfoCircleIcon } from 'src/icons';
import { getFromStorage } from 'src/lib/storage';
import {
  addPromptAction,
  getAllConditionPromptAction,
  getAllPromptScreenConditionAction,
} from 'src/modules/SuperAdmin/store/actions/prompts.action';
import { getConditionPromptSlice } from 'src/modules/SuperAdmin/store/slices/prompts.slice';
import * as Yup from 'yup';
import { SuperAdminModal } from '../../../baseComponents';
import { stringToColor } from './utils';

const AddPromptPage = ({
  isOpen,
  setIsOpen,
  selectedCondition,
  selectedSpecialty,
}) => {
  const initialValues = {
    condition_id: '',
    description: '',
    prompt_screen: '',
    max_token: null,
    prompt_name: '',
    llm_version: '',
    feature_flags: '{}',
    temp: '',
    top_p: '',
    num_questions: null,
  };

  const dispatch = useDispatch();
  const cachedUserEmail = getFromStorage(`user_email`);

  const {
    getLlMVersionsList,
    promptScreens,
    getConditionPrompt,
    PromptScreenCondition,
  } = useSelector((state) => state?.promptsState);
  const [conditionOptions, setConditionOptions] = useState([]);
  const [storeConditionId, setStoreConditionId] = useState(null);
  const [llmVersionOptions, setLlmVersionOptions] = useState([]);
  const [storeLlmVersion, setStoreLlmVersion] = useState(null);
  const [llmMaxTokenLimits, setLlmMaxTokenLimits] = useState({});
  const [isLlmVersionSelected, setLlmVersionSelected] = useState(false);
  const [isPromptScreenSelected, setPromptScreenSelected] = useState(false);
  const [promptScreenOptions, setPromptScreenOptions] = useState([]);
  const [description, setDescription] = useState(null);
  const [promptScreenName, setPromptScreenName] = useState(null);
  const [segmentedButtons, setSegmentedButtons] = useState([]);
  const [activeContent, setActiveContent] = useState('');
  const [activeButton, setActiveButton] = useState('');
  const [originalContent, setOriginalContent] = useState('');
  const [updatedPromptPayload, setUpdatedPromptPayload] = useState([]);
  const [storePromptScreenId, setStorePromptScreenId] = useState(null);

  useEffect(() => {
    if (PromptScreenCondition && Array.isArray(PromptScreenCondition)) {
      let dataArr = [];
      PromptScreenCondition.forEach((key) => {
        dataArr.push({
          value: key?.condition_id,
          label: key?.condition_name ? key?.condition_name : 'N/A',
        });
      });
      setConditionOptions(dataArr);
    }
  }, [PromptScreenCondition]);

  useEffect(() => {
    if (promptScreens && Array.isArray(promptScreens)) {
      let dataArr = [];
      promptScreens.forEach((key) => {
        dataArr.push({
          value: key?.prompt_screens_id,
          label: key?.prompt_screen,
          description: key?.description,
        });
      });
      setPromptScreenOptions(dataArr);
    }
  }, [promptScreens]);

  useEffect(() => {
    if (getLlMVersionsList && Array.isArray(getLlMVersionsList)) {
      let dataArr = [];
      let maxTokens = {};
      getLlMVersionsList.forEach((key) => {
        dataArr.push({
          value: key?.llm_version,
          label: key?.model ? key?.model : 'N/A',
          name: key?.max_tokens,
        });
        maxTokens[key?.llm_version] = key?.max_tokens;
      });
      setLlmVersionOptions(dataArr);
      setLlmMaxTokenLimits(maxTokens);
    }
  }, [getLlMVersionsList]);

  // Auto generated bg color for segmented buttons : End

  useEffect(() => {
    if (
      getConditionPrompt &&
      Array.isArray(getConditionPrompt) &&
      getConditionPrompt.length > 0
    ) {
      const keyCount = {};
      const seenContent = new Set();

      const buttons = getConditionPrompt
        .map((item) => {
          // Filter out keys that are 'prompt_id' or 'prompt_screens_id' from the BE response
          const filteredKeys = Object.keys(item).filter(
            (key) => key !== 'prompt_id' && key !== 'prompt_screens_id'
          );

          return filteredKeys.map((key) => {
            const uniqueKey = keyCount[key]
              ? `${key}_${item.prompt_screens_id}_${item.prompt_id}`
              : key;
            keyCount[key] = (keyCount[key] || 0) + 1;
            const finalButtonNames =
              uniqueKey.trim() === '' ? `No description` : key;

            return {
              label: finalButtonNames,
              value: uniqueKey,
              content: item[key],
              key: key,
              prompt_screen_id: item.prompt_screens_id,
            };
          });
        })
        .flat(); // Flatten the array to get a single level array of buttons

      // Filter out buttons with duplicate content
      const uniqueButtons = buttons.filter((button) => {
        if (seenContent.has(button.content)) {
          return false;
        }
        seenContent.add(button.content);
        return true;
      });

      // Add avatar text based on the length of uniqueButtons
      const buttonsWithAvatars = uniqueButtons.map((button, index) => {
        // Generate dynamic color based on the index and uniqueKey
        const dynamicBackgroundColor = stringToColor(
          `${index}_${button.value}`
        );

        return {
          ...button,
          label: (
            <div className="flex flex-col items-center">
              <Avatar
                size={24}
                className="text-sm flex items-center mt-1"
                style={{ backgroundColor: dynamicBackgroundColor }}
              >
                <div style={{ fontSize: '10px' }}>{index + 1}</div>
              </Avatar>
              <div className="mt-2">{button.label}</div>
            </div>
          ),
        };
      });

      setSegmentedButtons(buttonsWithAvatars);

      if (buttonsWithAvatars.length > 0) {
        setActiveButton(buttonsWithAvatars[0].value);
        setActiveContent(buttonsWithAvatars[0].content);
        setOriginalContent(buttonsWithAvatars[0].content); // Set the original content
      }
    }
  }, [getConditionPrompt]);

  const handleSegmentChange = (value) => {
    const selectedButton = segmentedButtons.find(
      (button) => button.value === value
    );
    setActiveButton(value);
    value === 'intake_summary'
      ? setActiveContent(
          updatedPromptPayload?.length > 0
            ? updatedPromptPayload[0]?.intake_summary
            : selectedButton
              ? selectedButton.content
              : ''
        )
      : setActiveContent(selectedButton ? selectedButton.content : '');
    setOriginalContent(selectedButton ? selectedButton.content : ''); // Set the original content
  };

  const handleContentChange = (e) => {
    const newValue = e.target.value;
    setActiveContent(newValue);

    // Check if the content is different from the original content
    if (newValue !== originalContent) {
      const selectedButton = segmentedButtons.find(
        (button) => button.value === activeButton
      );
      // Payload set here  : Starts
      if (selectedButton) {
        setUpdatedPromptPayload([
          {
            [selectedButton.key]: newValue,
            prompt_screens_id: selectedButton.prompt_screen_id,
          },
        ]);
      }
    } else {
      setUpdatedPromptPayload([]);
    }
    // Ends
  };

  const customScrollbarStyle = {
    overflowY: 'hidden',
    overflowX: 'auto',
    scrollbarWidth: 'thin',
    scrollbarColor: '#ccc transparent',
  };

  const sections = [
    {
      heading: (
        <div>
          <div>Add prompt</div>
          <div className=" w-4/6  bg-[#eefaf4] rounded-[10px] border-[1px] border-[#16A878] mt-4 px-3 py-2">
            <div className="flex my-1">
              <InfoCircleIcon className={`mr-1`} />
              <span className="ml-1 my-0.5 text-base font-medium text-[#000]">
                Information
              </span>
            </div>
            <div className="my-1 font-normal ml-9" style={{ fontSize: '13px' }}>
              If you want to add a new prompt screen, update directly in the
              database.
            </div>
          </div>
        </div>
      ),
      fields: [],
    },
    {
      className: 'flex gap-6 item-center',
      fields: [
        {
          name: 'feature_flags',
          type: 'custom',
          className: 'font-normal text-sm items-center justify-center',
          disabledField: true,
          render: () => (
            <span className="justify-center">
              <b>Feature flags :</b> {initialValues.feature_flags}
            </span>
          ),
        },

        {
          name: 'description',
          type: 'custom',
          disabled: true,
          className:
            'flex gap-1 font-normal text-sm items-center justify-center',
          render: () => (
            <span className="justify-center">
              <b>Description :</b> {description || ''}
            </span>
          ),
        },
      ],
    },
    {
      className: 'grid grid-cols-3 gap-2',
      fields: [
        {
          name: 'prompt_screen',
          type: 'select',
          title: 'Prompt screen',
          placeholder: 'Select prompt screen',
          options: promptScreenOptions,
          onChange: (setFieldValue, e, option) => {
            setPromptScreenSelected(true);
            setFieldValue('prompt_screen', e);
            setFieldValue('condition_id', '');
            setPromptScreenName(option?.label);
            setDescription(option?.description);
            setSegmentedButtons([]);
            setActiveContent('');
            setStorePromptScreenId(e);
            const prompt_payload = {
              prompt_screens_id: e,
            };
            dispatch(getAllPromptScreenConditionAction(prompt_payload));
          },
        },
        {
          name: 'condition_id',
          type: 'select',
          title: 'Condition',
          placeholder: 'Select condition',
          options: conditionOptions,
          disabled: isPromptScreenSelected ? false : true,
          onChange: (setFieldValue, e) => {
            const prompt_check_payload = {
              condition_id: e,
              prompt_screens_id: storePromptScreenId,
            };

            dispatch(getAllConditionPromptAction(prompt_check_payload));
            setFieldValue('condition_id', e);
            // setFieldValue("prompt_screen", "")
            setStoreConditionId(e);

            setSegmentedButtons([]);
          },
        },
        {
          name: 'llm_version',
          type: 'select',
          title: 'LLM version',
          placeholder: 'Select LLM version',
          options: llmVersionOptions,
          onChange: (setFieldValue, e) => {
            setLlmVersionSelected(true);
            setFieldValue('llm_version', e);
            setStoreLlmVersion(e);
          },
        },
      ],
    },
    {
      className:
        isPromptScreenSelected &&
        promptScreenName &&
        promptScreenName.toLowerCase().endsWith('chat')
          ? 'grid grid-cols-4 gap-1'
          : 'grid grid-cols-3 gap-2',
      fields: [
        {
          name: 'max_token',
          type: 'InputNumber',
          title: 'Max tokens',
          placeholder: 'Enter max tokens',
          disabled: isLlmVersionSelected ? false : true,
          options: llmVersionOptions,
          onChange: (setFieldValue, e) => {
            setFieldValue('max_token', e);
          },
        },
        {
          name: 'temp',
          type: 'text',
          title: 'Temp',
          placeholder: 'Enter temp',
        },
        {
          name: 'top_p',
          type: 'InputNumber',
          title: 'Top_p',
          placeholder: 'Enter top_p',
          onChange: (setFieldValue, e) => {
            setFieldValue('top_p', e);
          },
        },
        ...(isPromptScreenSelected &&
        promptScreenName &&
        promptScreenName.toLowerCase().endsWith('chat')
          ? [
              {
                name: 'num_questions',
                type: 'InputNumber',
                title: 'Num_questions',
                placeholder: 'Enter num_questions',
                onChange: (setFieldValue, e) => {
                  setFieldValue('num_questions', e);
                },
              },
            ]
          : []),
      ],
    },
    {
      fields: [
        {
          name: 'prompt_name',
          type: 'modalTextArea',
          title: 'Prompt',
          placeholder: 'Enter prompt',
        },
      ],
    },
    {
      heading: segmentedButtons?.length > 0 && (
        <div className="py-5">
          <div
            className="segmented_super_admin rounded-lg w-full mb-5 flex overflow-auto"
            style={{ ...customScrollbarStyle }}
          >
            <Segmented
              style={{ fontSize: '12px' }}
              options={segmentedButtons}
              onChange={handleSegmentChange}
              value={activeButton}
            />
          </div>
          <div>
            <TextArea
              className={`${activeButton.includes('prefix prompt') || activeButton.includes('suffix prompt') ? 'modal__form-el-field-textarea_reduced' : 'modal__form-el-field-textarea'}
              ${activeButton.startsWith('Intake summary prompt') ? ' mt-2' : ''}`}
              value={activeContent ? activeContent : ''}
              bordered={false}
              autoSize={{ minRows: 3, maxRows: 13 }}
              onChange={handleContentChange}
              readOnly
            />
          </div>
        </div>
      ),
      fields: [],
    },
  ];

  const validationSchemaField = Yup.object().shape({
    prompt_screen: Yup.string().required('Prompt screen is required'),
    condition_id: Yup.string().required('Condition is required'),
    llm_version: Yup.string().required('LLM version is required'),
    max_token: Yup.number()
      .typeError('Max tokens is required')
      .required('Max tokens is required')
      .test('Max tokens is required', (value) => value > 0)
      .test('max-token', function (value) {
        const { llm_version } = this.parent;
        const maxToken = llmMaxTokenLimits[llm_version];
        if (maxToken && value > maxToken) {
          return this.createError({
            message: `Max tokens exceeds the allowed limit of ${maxToken}`,
          });
        }
        return true;
      }),
    temp: Yup.string().required('Temp is required'),
    top_p: Yup.number()
      .typeError('Top_p must be a number')
      .required('Top_p is required')
      .test(
        'is-greater-than-zero',
        'Top_p must be greater than 0',
        (value) => value > 0
      ),
    num_questions: Yup.number()
      .typeError('Num_questions must be a number')
      .test(
        'is-greater-than-zero',
        'Num_questions must be greater than 0',
        (value) => value > 0
      ),
    prompt_name: Yup.string().required('Prompt is required'),
  });

  return (
    <SuperAdminModal
      open={isOpen}
      sections={sections}
      initialValues={initialValues}
      validationSchema={validationSchemaField}
      closable={true}
      centered={true}
      submitButtonText="Submit"
      cancelButtonText="Cancel"
      className={`prompt-modal`}
      handleCancel={() => {
        setIsOpen(false);
        setLlmVersionSelected(false);
        setPromptScreenSelected(false);
        setDescription('');
        setActiveContent('');
        setSegmentedButtons([]);
        dispatch(getConditionPromptSlice(null));
        setUpdatedPromptPayload([]);
        setConditionOptions([]);
      }}
      handleSubmit={(values) => {
        const payload = {
          condition_id: storeConditionId,
          description: description,
          prompt_screens_id: values?.prompt_screen,
          max_token: values?.max_token,
          prompt: values?.prompt_name,
          llm_id: storeLlmVersion,
          created_by: cachedUserEmail,
          temp: values?.temp || null,
          top_p: values?.top_p || null,
          num_questions: values?.num_questions,
          prompt_screen: promptScreenName,
        };
        dispatch(
          addPromptAction(
            payload,
            setIsOpen,
            setLlmVersionSelected,
            setDescription,
            setPromptScreenSelected,
            selectedSpecialty,
            selectedCondition
          )
        );
      }}
    />
  );
};

export default AddPromptPage;
